/**
 * How a disclosure agreement reads on figures S5 and S7 — one definition, so
 * the entity screen and the across-portfolios screen can never disagree about
 * the same agreement.
 *
 * WHICH SIDE IS "OURS" — and why it is not `my_org_id`.
 *
 * The agreement row carries a per-CALLER field, `my_org_id`, which the backend
 * fills in with whichever party it resolved this request against. For an
 * ordinary agreement — one of our entities and one outside organisation —
 * that is unambiguous. For an agreement between TWO of this portfolio's own
 * entities it is not: both parties are ours, and the backend's pick is not
 * stable across requests. Measured on the dev estate on 6 Sep 2026, agreement
 * 8e037ac8 (Meridian Process Systems ↔ Vanta Materials Group) came back with
 * `my_org_id` set to one side in one render and the other side 51 seconds
 * later, so the same row named a different counterparty each time and its
 * "Awaiting … consent" line named the party in its own Counterparty column.
 *
 * So the side assignment here is derived from the AGREEMENT, never from the
 * caller's perspective:
 *   the SUBJECT     → on a page ABOUT one entity (S5), that entity is always
 *                      the "entity" side and the other party is always the
 *                      counterparty. Round 2 shipped without this: every row
 *                      of Vanta Materials Group's own Disclosures card printed
 *                      "Vanta Materials Group" in the Counterparty column,
 *                      because both parties were in this portfolio and org_a
 *                      happened to be Vanta. A counterparty column that names
 *                      the page's own subject tells the reader nothing.
 *   both sides ours  → org_a is the entity, org_b the counterparty. `org_a` is
 *                      the initiating party and is a property of the record,
 *                      identical on every request. This is what S7 uses, where
 *                      there is no single subject entity.
 *   one side ours    → that side is the entity, the other is the counterparty.
 *   neither ours     → fall back to my_org_id; there is nothing better, and
 *                      this should not reach these screens at all.
 *
 * The consent levels follow the same rule: they are read from the ABSOLUTE
 * `org_a_consented_level` / `org_b_consented_level` fields, which are labelled
 * by side and therefore stable, rather than from the per-caller
 * `my_consented_level` / `counterparty_consented_level` pair, which is keyed
 * to `my_org_id` and would invert whenever that flips.
 *
 * Pure: no React, no fetching.
 */
import type { DisclosureAgreement } from '../../types'
import { IDENTITY_RUNG } from './RungMeter'

export type DisclosureStatusLabel = 'Consent owed' | 'NDA pending' | 'Active' | 'Interest expressed'

export interface DisclosureSides {
  /** The party this portfolio owns (or, failing that, the caller's own side). */
  entityId: string
  /** The other party. */
  counterpartyId: string
  /** Both parties are this portfolio's own entities. */
  betweenOwn: boolean
  /** How far the entity side has consented. */
  entityConsented: number
  /** How far the other side has consented. */
  counterpartyConsented: number
}

/**
 * Decide, deterministically and from the agreement alone, which party is the
 * entity and which is the counterparty. Exported so the rule can be tested
 * directly, including the both-sides-ours case that used to flip.
 */
export function resolveSides(
  agreement: DisclosureAgreement,
  own: Set<string>,
  /**
   * The entity whose PAGE this is, when there is one (S5). Whichever party is
   * in this set becomes the entity side, so the counterparty column always
   * names the other party. Omitted on S7, which is about no single entity.
   */
  subject?: Set<string>,
): DisclosureSides {
  const aIsOurs = own.has(agreement.org_a_id)
  const bIsOurs = own.has(agreement.org_b_id)
  const betweenOwn = aIsOurs && bIsOurs

  const aIsSubject = !!subject?.has(agreement.org_a_id)
  const bIsSubject = !!subject?.has(agreement.org_b_id)

  let entityId: string
  // The page's own subject wins, unless BOTH parties are it (two owners of the
  // same entity group on one ladder), where org_a keeps the row deterministic.
  if (aIsSubject && !bIsSubject) entityId = agreement.org_a_id
  else if (bIsSubject && !aIsSubject) entityId = agreement.org_b_id
  else if (betweenOwn) entityId = agreement.org_a_id
  else if (aIsOurs) entityId = agreement.org_a_id
  else if (bIsOurs) entityId = agreement.org_b_id
  else entityId = agreement.my_org_id ?? agreement.org_a_id

  const counterpartyId = entityId === agreement.org_a_id ? agreement.org_b_id : agreement.org_a_id
  const entityIsA = entityId === agreement.org_a_id

  // The side-labelled levels first; the per-caller pair only as a fallback,
  // and then read through my_org_id so it is not silently mismatched.
  const aLevel = agreement.org_a_consented_level
  const bLevel = agreement.org_b_consented_level
  const haveAbsolute = typeof aLevel === 'number' && typeof bLevel === 'number'
  const callerIsEntity = agreement.my_org_id === entityId
  const perspectiveMine = agreement.my_consented_level ?? agreement.current_level
  const perspectiveTheirs = agreement.counterparty_consented_level ?? agreement.current_level

  const entityConsented = haveAbsolute
    ? (entityIsA ? aLevel : bLevel)
    : (callerIsEntity ? perspectiveMine : perspectiveTheirs)
  const counterpartyConsented = haveAbsolute
    ? (entityIsA ? bLevel : aLevel)
    : (callerIsEntity ? perspectiveTheirs : perspectiveMine)

  return { entityId, counterpartyId, betweenOwn, entityConsented, counterpartyConsented }
}

export interface DisclosureRow {
  agreement: DisclosureAgreement
  /** The portfolio's own entity on this agreement, named. */
  entityLabel: string
  /** The other side — a real name only from rung 4, after a mutual NDA. */
  counterpartyLabel: string
  /** A second line under the counterparty, when there is something true to say. */
  counterpartyNote?: string
  /** Both parties are this portfolio's own entities. */
  betweenOwn: boolean
  level: number
  ndaPending: boolean
  rungCaption: string
  status: DisclosureStatusLabel
  pendingAction: string
}

/**
 * "Consent owed" / "NDA pending" / "Interest expressed" / "Active" and the
 * pending action, derived from the AGREEMENT and nothing else.
 *
 * WHOSE CONSENT IS OUTSTANDING IS A FACT ABOUT THE RECORD, NOT ABOUT THE
 * READER. Round 7 shipped this deriving both from "the entity side", which is
 * whichever party the page is about — so agreement 75a01269 (Meridian at rung
 * 3, Vanta at rung 4, NDA pending) read "Awaiting Vanta Materials Group
 * consent" on Vanta's own page and "Awaiting Meridian Process Systems consent"
 * on the across-portfolios screen. Only one of those was true: Vanta has
 * consented one rung FURTHER, so the consent outstanding is Meridian's.
 *
 * So the party behind is the one with the lower SIDE-LABELLED consent level,
 * and it is named on every screen. The status follows the same rule, for the
 * same reason: the same record used to read "Active" on one screen and
 * "Consent owed" on another.
 *
 * MASKING IS UNCHANGED. The name used is the label the row itself prints, so
 * an external counterparty below rung 4 is "Anonymous organisation" here too —
 * this never says a name the row does not already say.
 */
export interface DisclosureSideLabels {
  entity: string
  counterparty: string
}

export function disclosureStatus(
  agreement: DisclosureAgreement,
  labels: string | DisclosureSideLabels,
  sides?: DisclosureSides,
): { status: DisclosureStatusLabel; pendingAction: string } {
  const resolved = sides ?? resolveSides(agreement, new Set([agreement.my_org_id ?? agreement.org_a_id]))
  const named = typeof labels === 'string' ? { entity: labels, counterparty: '' } : labels
  const say = (label: string) => (label && label !== '—' ? label : 'this entity')

  // The side that has consented LESS is the side a consent is waiting on.
  const behind = resolved.entityConsented < resolved.counterpartyConsented
    ? say(named.entity)
    : resolved.counterpartyConsented < resolved.entityConsented
      ? say(named.counterparty)
      : null

  if (agreement.nda_status === 'pending') {
    return {
      status: 'NDA pending',
      // Level with each other and an NDA still open: nobody is behind on the
      // ladder, the gate itself is what is outstanding. Naming a party here
      // would be a guess.
      pendingAction: behind ? `Awaiting ${behind} consent` : 'Awaiting the mutual NDA',
    }
  }
  if (behind) return { status: 'Consent owed', pendingAction: `Awaiting ${behind} consent` }
  if (agreement.current_level <= 1) return { status: 'Interest expressed', pendingAction: '—' }
  return { status: 'Active', pendingAction: '—' }
}

export interface BuildDisclosureRowsOptions {
  /** entity_id -> the name this portfolio holds for it. */
  entityNameById: Map<string, string>
  /** Only these agreements' own side is named; anything else stays anonymous. */
  ownEntityIds?: Set<string>
  /**
   * The entity this page is ABOUT (S5 passes its owners' ids). Its side is
   * always the entity, so the Counterparty column always names the OTHER
   * party rather than the page's own subject.
   */
  subjectEntityIds?: Set<string>
}

export function buildDisclosureRows(
  agreements: DisclosureAgreement[],
  { entityNameById, ownEntityIds, subjectEntityIds }: BuildDisclosureRowsOptions,
): DisclosureRow[] {
  const own = ownEntityIds ?? new Set(entityNameById.keys())
  // Ordered by agreement_id so two renders of the same data list the same rows
  // in the same order — the payload's own order is not guaranteed.
  return [...agreements]
    .sort((a, b) => a.agreement_id.localeCompare(b.agreement_id))
    .map((agreement) => {
      const sides = resolveSides(agreement, own, subjectEntityIds)
      const entityLabel = entityNameById.get(sides.entityId) ?? sides.entityId ?? '—'
      const named = agreement.current_level >= IDENTITY_RUNG && !!agreement.counterparty_name

      const counterpartyLabel = sides.betweenOwn
        ? entityNameById.get(sides.counterpartyId) ?? sides.counterpartyId
        : named
          ? (agreement.counterparty_name as string)
          : 'Anonymous organisation'
      const counterpartyNote = sides.betweenOwn
        ? 'your entity — both sides in this portfolio'
        : named
          ? `named at rung ${IDENTITY_RUNG}`
          : `identity from rung ${IDENTITY_RUNG}, after a mutual NDA`

      const ndaPending = agreement.nda_status === 'pending'
      const { status, pendingAction } = disclosureStatus(
        agreement, { entity: entityLabel, counterparty: counterpartyLabel }, sides,
      )
      return {
        agreement,
        entityLabel,
        counterpartyLabel,
        counterpartyNote,
        betweenOwn: sides.betweenOwn,
        level: agreement.current_level,
        ndaPending,
        rungCaption: ndaPending
          ? `rung ${agreement.current_level} · NDA gate`
          : `rung ${agreement.current_level}`,
        status,
        pendingAction,
      }
    })
}

/**
 * How many of THIS portfolio's own entities are a party to a live disclosure.
 *
 * ONE definition, used by S6's KPI tile and S7's table header, because round 2
 * shipped two: S6 counted distinct `my_org_id` values and said "across 3
 * entities", while S7's own table showed two entity names. `my_org_id` is a
 * per-caller field and is not stable when both parties belong to this
 * portfolio (see the note at the top of this file), so counting it was never
 * counting entities. This counts BOTH sides of every agreement and keeps the
 * ones this portfolio owns — an entity on either side of a ladder is on it.
 */
export function countEntitiesOnLadders(
  agreements: DisclosureAgreement[],
  ownEntityIds: Set<string>,
): number {
  const seen = new Set<string>()
  for (const a of agreements) {
    if (ownEntityIds.has(a.org_a_id)) seen.add(a.org_a_id)
    if (ownEntityIds.has(a.org_b_id)) seen.add(a.org_b_id)
  }
  return seen.size
}

/**
 * "across 3 of your entities" — the same words wherever the count appears.
 * "1 of your entities" is right at one, so there is no plural to switch on.
 */
export function entitiesOnLaddersLine(count: number): string {
  return `across ${count} of your entities`
}

/** The tone each status pill carries. */
export const DISCLOSURE_STATUS_TONE: Record<DisclosureStatusLabel, string> = {
  'Consent owed': 'bg-amber-100 text-amber-800',
  'NDA pending': 'bg-amber-100 text-amber-800',
  Active: 'bg-emerald-100 text-emerald-700',
  'Interest expressed': 'bg-gray-100 text-gray-600',
}

/**
 * The footnote both figures print under the table.
 *
 * The exemption is not a nicety. Five of the seven live rows on the dev estate
 * are between two of this portfolio's OWN entities, and both of those are named
 * in the table at rungs 1-4 — correctly, because you already know both of them.
 * Round 2's sentence stopped at "after a mutual NDA", so it read as a rule the
 * table in front of the reader was breaking.
 */
export const DISCLOSURE_FOOTNOTE =
  'Only the entity can consent — you can watch, never advance. · Identities appear from rung 4, '
  + 'after a mutual NDA — except where both sides are your own entities, which you already know by name.'
